import React from 'react';
import { AgentMark } from '../../ui/AgentMark';
import { ProcessingTag } from '../../ui/ProcessingTag';
import { Toggle } from '../../ui/Toggle';
import { agents } from '../../../data/agents';
import { useDb } from '../../../hooks/useDb';
import { euro } from '../../../utils/time';
import type { AgentId } from '../../../types/anna';
interface AgentRosterProps {
  paused: Record<string, boolean>;
  onPause: (agent: AgentId, paused: boolean) => void;
}
const STATUS = {
  bezig: {
    label: 'aan het werk',
    dot: 'bg-accent'
  },
  wacht: {
    label: 'wacht op jou',
    dot: 'bg-amber'
  },
  klaar: {
    label: 'klaar',
    dot: 'bg-white/30'
  },
  rust: {
    label: 'in rust',
    dot: 'bg-white/15'
  },
  gepauzeerd: {
    label: 'gepauzeerd',
    dot: 'bg-white/15'
  }
} as const;

/** All agents: what they are doing now, on which model, and what they cost today. */
export function AgentRoster({
  paused,
  onPause
}: AgentRosterProps) {
  const tasks = useDb(t => t.agentTasks);
  const costs = useDb(t => t.cloudCostByAgent);
  return <ul className="divide-y divide-white/[0.06] rounded-3xl border border-white/[0.07] bg-night-900 px-5" data-id="element-32" data-mp="1bsd8qeiwxvdd">
      {agents.map(a => {
      const task = tasks.find(t => t.agent === a.id && t.status !== 'klaar') ?? tasks.find(t => t.agent === a.id);
      const isPaused = paused[a.id] ?? false;
      const status = isPaused ? STATUS.gepauzeerd : task ? STATUS[task.status] : STATUS.rust;
      const cost = costs.find(c => c.agent === a.id)?.euro ?? 0;
      return <li key={a.id} className="grid items-center gap-x-6 gap-y-2 py-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_7rem_auto]" data-id="element-33" data-mp="1pqth09wfbtxm">
            <div className="flex min-w-0 items-center gap-3" data-id="element-34" data-mp="1kq6skpzhqwjn">
              <AgentMark agentId={a.id} data-id="element-35" data-mp="1gr6axasr1zy8" />
              <div className="min-w-0" data-id="element-36" data-mp="1gq4xx0s6b3dv">
                <p className="text-[15px] font-medium text-ink" data-id="element-37" data-mp="9dcp9nxffeb7">{a.name}</p>
                <p className="truncate text-[13px] text-ink-muted" data-id="element-38" data-mp="9ai7k45ss7lh">{a.scope}</p>
              </div>
            </div>
            <div className="min-w-0 text-[13px]" data-id="element-39" data-mp="6soig0rnabdr">
              <p className="flex items-center gap-2 text-ink-soft" data-id="element-40" data-mp="6sy7nm4v8eie">
                <span className={`h-2 w-2 shrink-0 rounded-full ${status.dot}`} aria-hidden data-id="element-41" data-mp="brro9nfg8lms" />
                {status.label}
                {task && !isPaused && <span className="truncate text-ink-muted" data-id="element-42" data-mp="3t1po80ml469c">· {task.title}</span>}
              </p>
              {task && <p className="mt-1 flex items-center gap-2 text-ink-muted" data-id="element-43" data-mp="3k7ba41dsj1rt">
                  <span className="truncate" data-id="element-44" data-mp="3k58duzm1ocen">{task.model}</span>
                  <ProcessingTag where={task.where} data-id="element-45" data-mp="3oytez0ixpmi2" />
                </p>}
            </div>
            <p className="text-[13px] tabular-nums text-ink-muted md:text-right" data-id="element-46" data-mp="3aagim2fxq1n1">
              <span className="text-ink-soft" data-id="element-47" data-mp="3f99vbsnedom5">{euro(cost)}</span> cloud vandaag
            </p>
            <div className="md:justify-self-end" data-id="element-48" data-mp="318765713vy70">
              <Toggle label="Actief" checked={!isPaused} onChange={v => onPause(a.id, !v)} data-id="element-49" data-mp="31ajuq31dd6t1" />
            </div>
          </li>;
    })}
    </ul>;
}